import { resolveReportGoOfficeCode } from "./adminCompany";

export const REPORT_LEVEL_OPTIONS = [
  { value: "1", label: "PITC " },
  { value: "2", label: "Company" },
  { value: "3", label: "Circle" },
  { value: "4", label: "Division" },
  { value: "5", label: "Sub-Division" },
  { value: "6", label: "Hierarchical" },
];

export const HIERARCHICAL_REPORT_LEVEL = "6";

export const normalizeUserHierarchy = (hierarchyLevel) => {
  const level = Number(hierarchyLevel);
  if (level === 52) {
    return 5;
  }
  return level;
};

export const getEffectiveUserHierarchy = (
  hierarchyValue,
  cfcLevelId,
  userHierarchy,
  userCode,
) => {
  if (String(userCode) === "10") {
    return 1;
  }
  if (Number(userHierarchy) === 52 || Number(userHierarchy) === 5) {
    return 5;
  }
  const raw = Number(hierarchyValue || cfcLevelId || userHierarchy) || 2;
  return normalizeUserHierarchy(raw);
};

export const getDefaultReportLevel = (userCode, effectiveHierarchy) => {
  if (String(userCode) === "10") {
    return "1";
  }
  if (effectiveHierarchy >= 5) {
    return HIERARCHICAL_REPORT_LEVEL;
  }
  if (effectiveHierarchy === 4) {
    return "4";
  }
  if (effectiveHierarchy === 3) {
    return "3";
  }
  return "2";
};

export const getAvailableReportLevelOptions = (userCode, effectiveHierarchy) => {
  if (String(userCode) === "10") {
    return REPORT_LEVEL_OPTIONS;
  }

  if (effectiveHierarchy === 2) {
    return REPORT_LEVEL_OPTIONS.filter((option) => option.value !== "1");
  }
  if (effectiveHierarchy === 3) {
    return REPORT_LEVEL_OPTIONS.filter((option) =>
      ["3", "4", "5", HIERARCHICAL_REPORT_LEVEL].includes(option.value),
    );
  }
  if (effectiveHierarchy === 4) {
    return REPORT_LEVEL_OPTIONS.filter((option) =>
      ["4", "5", HIERARCHICAL_REPORT_LEVEL].includes(option.value),
    );
  }
  if (effectiveHierarchy >= 5) {
    return REPORT_LEVEL_OPTIONS.filter((option) =>
      [HIERARCHICAL_REPORT_LEVEL].includes(option.value),
    );
  }

  return REPORT_LEVEL_OPTIONS.filter((option) => option.value !== "1");
};

export const resolveAuthOfficeCode = (userCode, effectiveHierarchy) => {
  const authCode = String(userCode ?? "");
  if (!authCode) {
    return "";
  }
  if (effectiveHierarchy <= 1 || authCode === "10") {
    return authCode;
  }
  if (effectiveHierarchy === 2) {
    return authCode.slice(0, 2);
  }
  if (effectiveHierarchy === 3) {
    return authCode.slice(0, 3);
  }
  if (effectiveHierarchy === 4) {
    return authCode.slice(0, 4);
  }
  return authCode.slice(0, 5);
};

export const isHierarchicalReportLevelValue = (reportLevelValue) =>
  Number(reportLevelValue) === Number(HIERARCHICAL_REPORT_LEVEL);

export const resolveApmsReportOfficeCode = ({
  userCode,
  effectiveHierarchy,
  reportLevelValue,
  officeCode,
  selectedCompanyCode,
  selectedAdminCompanyCode,
  circleCode,
  selectedDivision,
  selectedSubDivision,
}) => {
  const isAdminUser = String(userCode) === "10";
  const isHierarchical = isHierarchicalReportLevelValue(reportLevelValue);

  if (isAdminUser) {
    if (!isHierarchical) {
      return (
        selectedCompanyCode ||
        String(selectedAdminCompanyCode || "") ||
        officeCode ||
        "10"
      );
    }
    return resolveReportGoOfficeCode({
      code: userCode,
      officeCode,
      selectedCompanyCode,
      selectedAdminCompanyCode,
      circleCode,
      selectedDivision,
      selectedSubDivision,
    });
  }

  if (!isHierarchical) {
    return resolveAuthOfficeCode(userCode, effectiveHierarchy);
  }

  if (effectiveHierarchy >= 5) {
    return resolveAuthOfficeCode(userCode, effectiveHierarchy);
  }

  if (selectedSubDivision) {
    return selectedSubDivision;
  }
  if (selectedDivision) {
    return selectedDivision;
  }
  if (circleCode) {
    return circleCode;
  }

  return resolveAuthOfficeCode(userCode, effectiveHierarchy);
};

export const getReportLevelColumnLabel = (reportLevelValue, officeCodeValue) => {
  const level = Number(reportLevelValue);
  if (level === 2) {
    return String(officeCodeValue) === "10" ? "DISCO Name" : "Company Name";
  }
  if (level === 3) return "Circle Name";
  if (level === 4) return "Division Name";
  return "Sub Division Name";
};

export const buildUniqueCircles = (items = []) =>
  items.reduce((acc, curr) => {
    if (!acc.some((item) => item.circleCode === curr.circleCode)) {
      acc.push({
        circleCode: curr.circleCode,
        circleName: curr.circleName,
        companyCode: curr.companyCode,
      });
    }
    return acc;
  }, []);

export const buildUniqueDivisions = (items = []) =>
  items.reduce((acc, item) => {
    if (!acc.some((division) => division.divisionCode === item.divisionCode)) {
      acc.push({
        divisionCode: item.divisionCode,
        divisionName: item.divisionName,
        companyCode: item.companyCode,
      });
    }
    return acc;
  }, []);

export const buildUniqueSubDivisions = (items = []) =>
  items.reduce((acc, item) => {
    if (!acc.some((sub) => sub.subDivisionCode === item.subDivisionCode)) {
      acc.push({
        subDivisionCode: item.subDivisionCode,
        subDivisionName: item.subDivisionName,
        companyCode: item.companyCode,
      });
    }
    return acc;
  }, []);

const filterDivisionsByCircle = (items = [], circleCode) =>
  buildUniqueDivisions(
    items.filter((item) => String(item.circleCode) === String(circleCode)),
  );

const filterSubDivisionsByDivision = (items = [], divisionCode) =>
  buildUniqueSubDivisions(
    items.filter((item) => String(item.divisionCode) === String(divisionCode)),
  );

const filterSubDivisionsByCircle = (items = [], circleCode) =>
  buildUniqueSubDivisions(
    items.filter((item) => String(item.circleCode) === String(circleCode)),
  );

export const applyScopedHierarchyFilterLists = ({
  items,
  effectiveHierarchy,
  authCode,
  setCircleCode,
  setSelectedDivision,
  setSelectedSubDivision,
  setFilteredCircles,
  setFilteredDivisions,
  setFilteredSubDivisions,
}) => {
  const scopedCode = String(authCode ?? "");

  if (effectiveHierarchy === 3) {
    const circle = scopedCode.slice(0, 3);
    setCircleCode?.(circle);
    setFilteredCircles?.(buildUniqueCircles(items));
    setFilteredDivisions?.(filterDivisionsByCircle(items, circle));
    setFilteredSubDivisions?.(filterSubDivisionsByCircle(items, circle));
    return;
  }

  if (effectiveHierarchy === 4) {
    const division = scopedCode.slice(0, 4);
    setSelectedDivision?.(division);
    setFilteredCircles?.(buildUniqueCircles(items));
    setFilteredDivisions?.(filterDivisionsByCircle(items, scopedCode.slice(0, 3)));
    setFilteredSubDivisions?.(filterSubDivisionsByDivision(items, division));
    return;
  }

  if (effectiveHierarchy >= 5) {
    const division = scopedCode.slice(0, 4);
    const subDivision = scopedCode.slice(0, 5);
    setSelectedDivision?.(division);
    setSelectedSubDivision?.(subDivision);
    setFilteredSubDivisions?.(filterSubDivisionsByDivision(items, division));
    return;
  }

  setFilteredCircles?.(buildUniqueCircles(items));
  setFilteredDivisions?.(buildUniqueDivisions(items));
  setFilteredSubDivisions?.(buildUniqueSubDivisions(items));
};

export const getHierarchyFilterEnabledState = ({
  reportLevel,
  code,
  effectiveHierarchy,
  circleCode,
  selectedDivision,
}) => {
  const isHierarchical = isHierarchicalReportLevelValue(reportLevel);
  const isAdminUser = String(code) === "10";

  return {
    showCircleFilter: isHierarchical && (isAdminUser || effectiveHierarchy <= 2),
    showDivisionFilter: isHierarchical && (isAdminUser || effectiveHierarchy <= 3),
    showSubDivisionFilter: isHierarchical && (isAdminUser || effectiveHierarchy <= 4),
    circleFilterEnabled: isHierarchical && (isAdminUser || effectiveHierarchy <= 2),
    divisionFilterEnabled: isHierarchical
      ? isAdminUser || effectiveHierarchy === 2
        ? Boolean(circleCode)
        : effectiveHierarchy === 3
      : false,
    subDivisionFilterEnabled: isHierarchical
      ? effectiveHierarchy >= 4 || Boolean(selectedDivision)
      : false,
  };
};

export const filterDataByReportLevel = (
  data,
  officeCode,
  reportLevelValue,
  getCode = (item) => item.OfficeCode ?? item.officeCode,
) => {
  if (!Array.isArray(data)) {
    return [];
  }
  if (!data.length) {
    return data;
  }

  const level = Number(reportLevelValue);
  const targetCode = String(officeCode ?? "");

  // Levels 1–5: request office code already matches the selected report level.
  // The API returns rows for that scope — do not strip them client-side.
  if (level >= 1 && level <= 5) {
    return data;
  }

  // Hierarchical (6): show immediate children one level below the office code.
  if (targetCode.length === 5) {
    return data.filter((item) => String(getCode(item) ?? "") === targetCode);
  }

  return data.filter((item) => {
    const itemCode = String(getCode(item) ?? "");
    return (
      itemCode.startsWith(targetCode) &&
      itemCode.length === targetCode.length + 1
    );
  });
};
